import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../lib/api'
import { openSpvPdf, downloadAnafXml } from '../lib/openSpvPdf'
import { useAuthStore } from '../stores/auth'
import { Card } from '../components/ui/Card'
import { Field } from '../components/ui/Field'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { Pagination, type PageSize } from '../components/ui/Pagination'
import { DateRangeField } from '../components/ui/DateField'
import { downloadXlsx } from '../lib/downloadXlsx'
import { Download } from 'lucide-react'

interface AnafMessage {
  id: number
  anafMessageId: string
  idSolicitare: string | null
  type: string
  dataCreare: string
  cifPartener: string | null
  partenerName: string | null
  docId: string | null
  issueDate: string | null
  totalWithTax: string | null
  taxAmount: string | null
  currency: string | null
  downloadedAt: string | null
  parsedAt: string | null
  shopifyOrderId: number | null
  detalii: string | null
  errRefDocId: string | null
  errResolvedAt: string | null
  errResolveNote: string | null
}

interface ListResp {
  messages: AnafMessage[]
  total: number
  totals: { totalWithTax: string; taxAmount: string }
}

const TYPE_LABELS: Record<string, string> = {
  'FACTURA PRIMITA': 'Primită',
  'FACTURA TRIMISA': 'Trimisă',
  'ERORI FACTURA': 'Erori',
}

const TYPE_COLORS: Record<string, string> = {
  'FACTURA PRIMITA': 'bg-b4l-blue-light text-b4l-blue-dark',
  'FACTURA TRIMISA': 'bg-b4l-green-light text-b4l-green-dark',
  'ERORI FACTURA': 'bg-b4l-red-light text-b4l-red-dark',
}

type Category = 'all' | 'emise' | 'primite' | 'erori' | 'mesaje'

const CATEGORIES: { key: Category; label: string; activeClass: string }[] = [
  { key: 'all', label: 'Toate', activeClass: 'border-b4l-text bg-b4l-bg-soft' },
  {
    key: 'emise',
    label: 'Facturi emise',
    activeClass: 'border-b4l-green-dark bg-b4l-green-light/40',
  },
  {
    key: 'primite',
    label: 'Facturi primite',
    activeClass: 'border-b4l-blue-dark bg-b4l-blue-light/40',
  },
  { key: 'erori', label: 'Erori', activeClass: 'border-b4l-red-dark bg-b4l-red-light/40' },
  {
    key: 'mesaje',
    label: 'Mesaje cumpărător',
    activeClass: 'border-b4l-amber-dark bg-b4l-amber-light/40',
  },
]

export function EFacturaPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('anaf:write')

  const [search, setSearch] = useState('')
  const [type, setType] = useState('')
  const [category, setCategory] = useState<Category>('all')
  const [cifPartener, setCifPartener] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [errorStatus, setErrorStatus] = useState<'all' | 'unresolved' | 'resolved'>('all')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<PageSize>(10)
  const [detailId, setDetailId] = useState<number | null>(null)

  const params = new URLSearchParams()
  params.set('limit', String(pageSize))
  params.set('offset', String(page * pageSize))
  if (search.trim()) params.set('search', search.trim())
  if (category !== 'all') params.set('category', category)
  else if (type) params.set('type', type)
  if (cifPartener.trim()) params.set('cifPartener', cifPartener.trim())
  if (dateFrom) params.set('dateFrom', dateFrom)
  if (dateTo) params.set('dateTo', dateTo)
  if (category === 'erori' && errorStatus !== 'all') params.set('errorStatus', errorStatus)

  const query = useQuery<ListResp>({
    queryKey: ['anaf-messages', params.toString()],
    queryFn: async () => (await api.get<ListResp>(`/anaf/messages?${params.toString()}`)).data,
    refetchInterval: 60_000,
  })

  const detailQuery = useQuery<{ message: AnafMessage & { rawXml: string | null } }>({
    queryKey: ['anaf-message', detailId],
    queryFn: async () =>
      (
        await api.get<{ message: AnafMessage & { rawXml: string | null } }>(
          `/anaf/messages/${detailId}`,
        )
      ).data,
    enabled: detailId !== null,
  })

  const syncNow = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          ok: boolean
          sync?: { newMessages: number }
          download?: { downloaded: number }
          error?: { message: string }
        }>('/anaf/sync-now')
      ).data,
    onSuccess: (d) => {
      if (d.ok) {
        toast.success(
          `Sincronizare reușită: ${d.sync?.newMessages ?? 0} mesaje noi, ${d.download?.downloaded ?? 0} descărcate`,
        )
        queryClient.invalidateQueries({ queryKey: ['anaf-messages'] })
      } else {
        toast.error(d.error?.message ?? 'Eroare')
      }
    },
    onError: () =>
      toast.error(
        'Sincronizarea a eșuat. Verifică tokenul în Setări → Integrări → ANAF — token SPV.',
      ),
  })

  /*
    Importul mesajelor SPV dintr-un export al grilei ERP.

    ANAF listeaza mesajele doar pe ultimele 60 de zile, deci ianuarie si februarie 2026 n-au putut
    fi aduse niciodata — integrarea a pornit in martie. ERP-ul le avea, fiindca le descarcase la
    vremea lor, iar o factura care nu e aici nu poate fi legata de niciun eveniment.
  */
  const importEa = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      return (
        await api.post<{
          rezultat: {
            citite: number
            adaugate: number
            existente: number
            completate: number
            sarite: { rand: number; motiv: string }[]
            deLa: string | null
            panaLa: string | null
          }
        }>('/anaf/import-ea', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      ).data.rezultat
    },
    onSuccess: (r) => {
      const perioada = r.deLa && r.panaLa ? ` (${r.deLa} – ${r.panaLa})` : ''
      toast.success(
        `${r.adaugate} mesaje noi din ${r.citite} citite${perioada}. ` +
          `${r.existente} existau deja.`,
        { duration: 10_000 },
      )
      // Randurile sarite se SPUN, nu se ascund: altfel un import partial pare complet.
      if (r.sarite.length > 0) {
        toast.warning(
          `${r.sarite.length} rânduri sărite. Primul: rândul ${r.sarite[0].rand} — ${r.sarite[0].motiv}`,
          { duration: 12_000 },
        )
      }
      queryClient.invalidateQueries({ queryKey: ['anaf-messages'] })
      queryClient.invalidateQueries({ queryKey: ['anaf-pending'] })
    },
    onError: (e: unknown) => {
      const m = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data
        ?.error?.message
      toast.error(m ?? 'Importul a eșuat.')
    },
  })

  const downloadBatch = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ ok: boolean; downloaded: number; failed: number }>(
          '/anaf/download-batch?max=50',
        )
      ).data,
    onSuccess: (d) => {
      toast.success(`Descărcate: ${d.downloaded} XML, eșuate: ${d.failed}`)
      queryClient.invalidateQueries({ queryKey: ['anaf-messages'] })
      queryClient.invalidateQueries({ queryKey: ['anaf-pending-count'] })
    },
    onError: () => toast.error('Eroare la descărcare batch'),
  })

  const downloadAll = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ ok: boolean; jobsEnqueued: number; estimatedMessages: number }>(
          '/anaf/download-all',
        )
      ).data,
    onSuccess: (d) => {
      toast.success(
        `Descărcarea a fost programată (~${d.estimatedMessages} mesaje) și rulează în fundal. Reîncarcă pagina ca să vezi progresul.`,
      )
      // Refresh repetat in 1-2 minute pentru a vedea cum scade pending
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['anaf-pending-count'] })
        queryClient.invalidateQueries({ queryKey: ['anaf-messages'] })
      }, 30_000)
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['anaf-pending-count'] })
        queryClient.invalidateQueries({ queryKey: ['anaf-messages'] })
      }, 90_000)
    },
    onError: () => toast.error('Eroare la programarea job-urilor.'),
  })

  const pendingQuery = useQuery<{ pending: number; total: number }>({
    queryKey: ['anaf-pending-count'],
    queryFn: async () =>
      (await api.get<{ pending: number; total: number }>('/anaf/pending-count')).data,
    refetchInterval: 30_000,
  })

  const countsQuery = useQuery<{
    totals: { all: number; emise: number; primite: number; erori: number; mesaje: number }
  }>({
    queryKey: ['anaf-counts'],
    queryFn: async () =>
      (
        await api.get<{
          totals: { all: number; emise: number; primite: number; erori: number; mesaje: number }
        }>('/anaf/category-counts')
      ).data,
    refetchInterval: 30_000,
  })

  const total = query.data?.total ?? 0
  const totals = query.data?.totals
  const messages = query.data?.messages ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">eFactura → Mesaje SPV</h1>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={async () => {
              try {
                const exportParams = new URLSearchParams(params)
                exportParams.delete('limit')
                exportParams.delete('offset')
                await downloadXlsx(`/anaf/exports/messages?${exportParams.toString()}`)
                toast.success('Export descărcat')
              } catch {
                toast.error('Eroare export')
              }
            }}
          >
            <Download className="h-4 w-4" /> Export XLSX
          </Button>
          {canWrite && (
            <>
              {(pendingQuery.data?.pending ?? 0) > 0 && (
                <>
                  <Button
                    onClick={() => downloadBatch.mutate()}
                    loading={downloadBatch.isPending}
                    title="Descarcă XML-urile în așteptare (câte 50, ~30 de secunde)"
                  >
                    Descarcă 50 ({pendingQuery.data?.pending ?? 0})
                  </Button>
                  <Button
                    onClick={() => downloadAll.mutate()}
                    loading={downloadAll.isPending}
                    title="Descarcă până la 5000 în fundal (poate dura 5–10 minute)"
                  >
                    Descarcă tot
                  </Button>
                </>
              )}
              <Button
                variant="secondary"
                onClick={() => syncNow.mutate()}
                loading={syncNow.isPending}
              >
                Sincronizează acum
              </Button>
              {/*
                Eticheta poarta input-ul ascuns: ANAF nu mai da mesajele mai vechi de 60 de zile,
                deci singura cale catre ele e exportul grilei din ERP.
              */}
              <label
                className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-b4l-border px-3 py-2 text-sm hover:bg-b4l-bg-soft"
                title="Adaugă mesaje SPV dintr-un export al grilei eFactura SPV din ExpertAccounts — pentru perioadele pe care ANAF nu le mai listează"
              >
                {importEa.isPending ? 'Se importă…' : 'Import din ExpertAccounts'}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  disabled={importEa.isPending}
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    // Se goleste ca sa se poata reincarca acelasi fisier dupa o corectura.
                    e.target.value = ''
                    if (f) importEa.mutate(f)
                  }}
                />
              </label>
            </>
          )}
        </div>
      </div>

      {/* Tab-uri categorii */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => {
          const count = countsQuery.data?.totals?.[c.key] ?? 0
          const active = category === c.key
          return (
            <button
              key={c.key}
              onClick={() => {
                setCategory(c.key)
                setPage(0)
              }}
              className={`flex items-center gap-2 rounded border-2 px-3 py-2 text-sm transition ${
                active ? c.activeClass : 'border-b4l-border hover:bg-b4l-bg-soft'
              }`}
            >
              <span className="font-medium">{c.label}</span>
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] ${
                  active ? 'bg-white/70' : 'bg-b4l-bg text-b4l-text-muted'
                }`}
              >
                {count.toLocaleString('ro-RO')}
              </span>
            </button>
          )
        })}
      </div>

      {category === 'erori' && (
        <div className="flex flex-wrap gap-2">
          {(['all', 'unresolved', 'resolved'] as const).map((s) => (
            <button
              key={s}
              onClick={() => {
                setErrorStatus(s)
                setPage(0)
              }}
              className={`rounded border px-3 py-1.5 text-xs ${
                errorStatus === s
                  ? 'border-b4l-text bg-b4l-text text-white'
                  : 'border-b4l-border bg-white'
              }`}
            >
              {s === 'all'
                ? 'Toate erorile'
                : s === 'unresolved'
                  ? 'Doar nerezolvate'
                  : 'Doar rezolvate'}
            </button>
          ))}
        </div>
      )}

      <Card>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-5">
          <Field
            label="Căutare (nr factură / partener / index încărcare / mesaj SPV)"
            placeholder="ex: SRT-094067, Farmacia Tei, 6299962267"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(0)
            }}
          />
          <Field
            label="CIF partener"
            placeholder="ex: 12345678"
            value={cifPartener}
            onChange={(e) => {
              setCifPartener(e.target.value)
              setPage(0)
            }}
          />
          <div className="md:col-span-2">
            <DateRangeField
              fromValue={dateFrom}
              toValue={dateTo}
              onChange={(from, to) => {
                setDateFrom(from)
                setDateTo(to)
                setPage(0)
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-b4l-text-dim">Tip</label>
            <select
              value={type}
              onChange={(e) => {
                setType(e.target.value)
                setPage(0)
              }}
              className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
            >
              <option value="">Toate</option>
              <option value="FACTURA PRIMITA">Primite</option>
              <option value="FACTURA TRIMISA">Trimise</option>
              <option value="ERORI FACTURA">Erori</option>
            </select>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Kpi label="Mesaje (filtru)" value={total.toLocaleString('ro-RO')} />
        <Kpi
          label="Total cu TVA (RON)"
          value={Number(totals?.totalWithTax ?? '0').toLocaleString('ro-RO', {
            maximumFractionDigits: 0,
          })}
        />
        <Kpi
          label="Total TVA (RON)"
          value={Number(totals?.taxAmount ?? '0').toLocaleString('ro-RO', {
            maximumFractionDigits: 0,
          })}
        />
        <Kpi label="Descărcate" value={messages.filter((m) => m.downloadedAt).length} />
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs font-semibold text-b4l-text-dim">
                <th className="px-2 py-2">Tip</th>
                <th className="px-2 py-2">Data SPV</th>
                <th className="px-2 py-2">Nr factură</th>
                <th className="px-2 py-2">Data emitere</th>
                <th className="px-2 py-2">Partener</th>
                <th className="px-2 py-2">CIF</th>
                <th className="px-2 py-2 text-right">Total cu TVA</th>
                <th className="px-2 py-2 text-right">TVA</th>
                <th className="px-2 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {query.isLoading && (
                <tr>
                  <td colSpan={9} className="px-2 py-6 text-center text-b4l-text-muted">
                    Se încarcă...
                  </td>
                </tr>
              )}
              {messages.length === 0 && !query.isLoading && (
                <tr>
                  <td colSpan={9} className="px-2 py-6 text-center text-b4l-text-muted">
                    Niciun mesaj. Verifică în Setări → ANAF dacă tokenul e configurat.
                  </td>
                </tr>
              )}
              {messages.map((m) => (
                <tr
                  key={m.id}
                  onClick={() => setDetailId(m.id)}
                  className="cursor-pointer border-b border-b4l-border hover:bg-b4l-bg-soft"
                >
                  <td className="px-2 py-1.5">
                    <span
                      className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
                        TYPE_COLORS[m.type] ?? 'bg-b4l-bg'
                      }`}
                    >
                      {TYPE_LABELS[m.type] ?? m.type}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-xs">
                    {new Date(m.dataCreare).toLocaleDateString('en-GB')}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs">{m.docId ?? '—'}</td>
                  <td className="px-2 py-1.5 text-xs">
                    {m.issueDate ? new Date(m.issueDate).toLocaleDateString('en-GB') : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-xs">{m.partenerName ?? '—'}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px]">{m.cifPartener ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right font-medium">
                    {m.totalWithTax
                      ? `${Number(m.totalWithTax).toFixed(2)} ${m.currency ?? 'RON'}`
                      : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right text-xs">
                    {m.taxAmount ? Number(m.taxAmount).toFixed(2) : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-xs">
                    {m.parsedAt ? (
                      <span className="text-b4l-green-dark">✓ procesată</span>
                    ) : m.downloadedAt ? (
                      <span className="text-b4l-amber-dark">⏳ descărcat</span>
                    ) : (
                      <span className="text-b4l-text-muted">⏸ în coadă</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3">
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s)
              setPage(0)
            }}
            itemLabel="mesaje"
          />
        </div>
      </Card>

      <Modal
        open={detailId !== null}
        onClose={() => setDetailId(null)}
        title="Detalii factură"
        size="xl"
      >
        {detailQuery.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă...</p>}
        {detailQuery.data && <MessageDetail data={detailQuery.data.message} canDownload={true} />}
      </Modal>
    </div>
  )
}

function MessageDetail({
  data,
  canDownload,
}: {
  data: AnafMessage & { rawXml: string | null }
  canDownload: boolean
}) {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('anaf:write')
  const isErr = data.type === 'ERORI FACTURA'
  const [refDocId, setRefDocId] = useState(data.errRefDocId ?? '')
  const [resolveNote, setResolveNote] = useState(data.errResolveNote ?? '')

  const resolveMutation = useMutation({
    mutationFn: async (resolved: boolean) =>
      (
        await api.patch(`/anaf/messages/${data.id}/error-resolution`, {
          resolved,
          note: resolveNote.trim() || null,
          refDocId: refDocId.trim() || null,
        })
      ).data,
    onSuccess: () => {
      toast.success('Status eroare actualizat')
      queryClient.invalidateQueries({ queryKey: ['anaf-message', data.id] })
      queryClient.invalidateQueries({ queryKey: ['anaf-messages'] })
    },
    onError: () => toast.error('Eroare la salvare'),
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <span
            className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
              TYPE_COLORS[data.type] ?? 'bg-b4l-bg'
            }`}
          >
            {TYPE_LABELS[data.type] ?? data.type}
          </span>
          <div className="mt-1 font-mono text-lg font-semibold">{data.docId ?? '—'}</div>
          <div className="text-xs text-b4l-text-muted">
            Mesaj SPV: {data.anafMessageId}
            {data.idSolicitare && (
              <>
                {' · '}
                Index încărcare: <span className="font-mono">{data.idSolicitare}</span>
              </>
            )}
            {' · '}
            primit: {new Date(data.dataCreare).toLocaleString('en-GB')}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-b4l-text-muted">Total cu TVA</div>
          <div className="text-2xl font-semibold">
            {data.totalWithTax
              ? `${Number(data.totalWithTax).toFixed(2)} ${data.currency ?? 'RON'}`
              : '—'}
          </div>
          {data.taxAmount && (
            <div className="text-xs text-b4l-text-muted">
              din care TVA: {Number(data.taxAmount).toFixed(2)} {data.currency ?? 'RON'}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded border border-b4l-border p-3">
          <div className="mb-2 text-xs font-semibold uppercase text-b4l-text-dim">Partener</div>
          <div className="text-sm font-medium">{data.partenerName ?? '—'}</div>
          <div className="text-xs text-b4l-text-muted">CIF: {data.cifPartener ?? '—'}</div>
        </div>
        <div className="rounded border border-b4l-border p-3">
          <div className="mb-2 text-xs font-semibold uppercase text-b4l-text-dim">Date</div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-b4l-text-muted">Emisă:</span>
              <span>
                {data.issueDate ? new Date(data.issueDate).toLocaleDateString('en-GB') : '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-b4l-text-muted">Descărcată:</span>
              <span>
                {data.downloadedAt ? new Date(data.downloadedAt).toLocaleDateString('en-GB') : '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-b4l-text-muted">Procesată:</span>
              <span>
                {data.parsedAt ? new Date(data.parsedAt).toLocaleDateString('en-GB') : '—'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {data.detalii && (
        <div className="rounded border border-b4l-amber-light bg-b4l-amber-light/30 p-3 text-xs">
          <strong>Detalii SPV:</strong> {data.detalii}
        </div>
      )}

      {isErr && (
        <div
          className={`rounded border p-3 text-xs ${
            data.errResolvedAt
              ? 'border-b4l-green-light bg-b4l-green-light/20'
              : 'border-b4l-red-light bg-b4l-red-light/20'
          }`}
        >
          <div className="mb-2 flex items-center justify-between">
            <strong>
              Status eroare:{' '}
              {data.errResolvedAt ? (
                <span className="text-b4l-green-dark">
                  ✓ Rezolvat ({new Date(data.errResolvedAt).toLocaleString('en-GB')})
                </span>
              ) : (
                <span className="text-b4l-red-dark">✗ Nerezolvat</span>
              )}
            </strong>
          </div>
          {canWrite && (
            <div className="space-y-2">
              <Field
                label="Nr factură referită (opțional, ex: SRT-094067)"
                value={refDocId}
                onChange={(e) => setRefDocId(e.target.value)}
              />
              <div>
                <label className="block text-xs font-medium text-b4l-text-dim">
                  Notă rezolvare (opțional)
                </label>
                <textarea
                  value={resolveNote}
                  onChange={(e) => setResolveNote(e.target.value)}
                  rows={2}
                  className="mt-1 w-full rounded border border-b4l-border bg-white px-2 py-1.5 text-xs"
                />
              </div>
              <div className="flex gap-2">
                {!data.errResolvedAt ? (
                  <Button
                    onClick={() => resolveMutation.mutate(true)}
                    loading={resolveMutation.isPending}
                  >
                    Marchează rezolvat
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() => resolveMutation.mutate(false)}
                    loading={resolveMutation.isPending}
                  >
                    Anulează rezolvarea
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {data.rawXml && (
        <FacturaPreview id={data.id} rawXml={data.rawXml} canDownload={canDownload} />
      )}
    </div>
  )
}

function FacturaPreview({
  id,
  rawXml,
  canDownload,
}: {
  id: number
  rawXml: string
  canDownload: boolean
}) {
  const [view, setView] = useState<'pdf' | 'xml'>('pdf')
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfErr, setPdfErr] = useState<string | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)

  // Fetch PDF as blob (axios with auth) — iframes nu primesc Authorization header
  useEffect(() => {
    if (view !== 'pdf') return
    if (pdfUrl || pdfErr || pdfLoading) return
    let revoke: string | null = null
    setPdfLoading(true)
    api
      .get(`/anaf/messages/${id}/pdf`, { responseType: 'blob', timeout: 60_000 })
      .then((resp) => {
        const blob = resp.data as Blob
        const url = URL.createObjectURL(blob)
        revoke = url
        setPdfUrl(url)
      })
      .catch((err: unknown) => {
        const msg =
          (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
            ?.message ?? 'Eroare la conversia PDF'
        setPdfErr(msg)
      })
      .finally(() => setPdfLoading(false))
    return () => {
      if (revoke) URL.revokeObjectURL(revoke)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, id])

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1">
          <button
            onClick={() => setView('pdf')}
            className={`rounded px-3 py-1 text-xs font-medium ${
              view === 'pdf'
                ? 'bg-b4l-blue text-white'
                : 'bg-b4l-bg text-b4l-text-muted hover:bg-b4l-bg-soft'
            }`}
          >
            PDF
          </button>
          <button
            onClick={() => setView('xml')}
            className={`rounded px-3 py-1 text-xs font-medium ${
              view === 'xml'
                ? 'bg-b4l-blue text-white'
                : 'bg-b4l-bg text-b4l-text-muted hover:bg-b4l-bg-soft'
            }`}
          >
            XML
          </button>
        </div>
        {canDownload && (
          <div className="flex gap-3 text-xs">
            <button
              type="button"
              onClick={() => openSpvPdf(id).catch(() => undefined)}
              className="text-b4l-blue-dark hover:underline"
            >
              Descarcă PDF ↓
            </button>
            <button
              type="button"
              onClick={() => downloadAnafXml(id).catch(() => undefined)}
              className="text-b4l-blue-dark hover:underline"
            >
              Descarcă XML →
            </button>
          </div>
        )}
      </div>

      {view === 'pdf' ? (
        <div className="rounded border border-b4l-border bg-b4l-bg-soft" style={{ height: 600 }}>
          {pdfLoading && (
            <div className="flex h-full items-center justify-center text-sm text-b4l-text-muted">
              Conversie XML → PDF prin ANAF (poate dura 5-10s)...
            </div>
          )}
          {pdfErr && (
            <div className="p-4 text-sm text-b4l-red-dark">
              Nu s-a putut genera PDF: {pdfErr}
              <div className="mt-2 text-xs text-b4l-text-muted">
                Deschide fila XML ca să vezi conținutul brut.
              </div>
            </div>
          )}
          {pdfUrl && !pdfErr && (
            <>
              {/* Desktop: PDF inline */}
              <iframe src={pdfUrl} title="Factura PDF" className="hidden h-full w-full sm:block" />
              {/* Mobil: browserele nu randează PDF în iframe → deschidere în tab (link real) */}
              <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center text-sm text-b4l-text-muted sm:hidden">
                <span>Pe telefon, apasă butonul ca să vezi PDF-ul.</span>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded bg-b4l-blue px-4 py-2 text-sm font-medium text-white"
                >
                  Deschide PDF
                </a>
              </div>
            </>
          )}
        </div>
      ) : (
        <pre className="max-h-[600px] overflow-y-auto rounded border border-b4l-border bg-b4l-bg-soft p-2 font-mono text-[10px]">
          {rawXml.length > 100_000 ? rawXml.slice(0, 100_000) + '\n...(truncat)' : rawXml}
        </pre>
      )}
    </div>
  )
}

function Kpi({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded border border-b4l-border bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-b4l-text-dim">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  )
}
